export const ONBOARDING_PATH = '/onboarding'

export const ONBOARDING_PROFILE_TYPES = ['venue', 'artist', 'promoter'] as const
export type OnboardingProfileType = (typeof ONBOARDING_PROFILE_TYPES)[number]

type AuthenticationDestination = 'login' | 'signup' | 'verify-email' | 'apply'

function getOnboardingProfileType(url: URL | string): OnboardingProfileType | null {
    const parsedUrl = typeof url === 'string' ? new URL(url) : url
    const intent = parsedUrl.searchParams.get('intent')

    return ONBOARDING_PROFILE_TYPES.find((profileType) => profileType === intent) ?? null
}

export function getOnboardingContinuation(url: URL | string): string {
    const profileType = getOnboardingProfileType(url)
    return profileType ? `${ONBOARDING_PATH}?type=${profileType}` : '/authenticated-entry'
}

export function getAuthenticationContinuation(
    destination: AuthenticationDestination,
    url: URL | string,
    email?: string,
): string {
    const searchParams = new URLSearchParams()
    const profileType = getOnboardingProfileType(url)

    if (profileType) searchParams.set('intent', profileType)
    if (email) searchParams.set('email', email)

    const search = searchParams.toString()
    return `/${destination}${search ? `?${search}` : ''}`
}

export function getVerificationCallbackPath(value: string | null, appUrl = 'https://self-service.entx.local'): string {
    if (!value) return '/authenticated-entry'
    const base = new URL(appUrl)
    let callback: URL
    try {
        callback = new URL(value, base)
    } catch {
        return '/authenticated-entry'
    }
    if (callback.origin !== base.origin || callback.pathname !== ONBOARDING_PATH) return '/authenticated-entry'
    const profileType = ONBOARDING_PROFILE_TYPES.find((type) => type === callback.searchParams.get('type'))
    return profileType ? `${ONBOARDING_PATH}?type=${profileType}` : '/authenticated-entry'
}

/**
 * Where a verification link sends an account below the Self-Service tier: `/apply`, carrying the
 * onboarding intent the link's `callbackURL` held, so a customer who started a venue flow resumes
 * it once they have applied. Validated the same way as the callback itself.
 */
export function getApplyPathFromVerificationCallback(value: string | null, appUrl?: string): string {
    const callback = new URL(getVerificationCallbackPath(value, appUrl), 'https://self-service.entx.local')
    const applyUrl = new URL('/apply', 'https://self-service.entx.local')
    const profileType = callback.searchParams.get('type')
    if (profileType) applyUrl.searchParams.set('intent', profileType)
    return getAuthenticationContinuation('apply', applyUrl)
}
